// =====================================================================
// CardFeeRulesDialog (Financeiro Cartões / S63) — gerencia as regras de taxa
// da maquininha (bandeira × tipo × faixa de parcelas → taxa % + prazo D+N).
// =====================================================================
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, IconButton, Tooltip, Box, Typography, CircularProgress
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { toast } from "react-toastify";
import {
  listCardFeeRules, upsertCardFeeRule, deleteCardFeeRule, CARD_BRANDS, financeErrorMessage
} from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";

const blank = { id: null, kind: "credito", brand: "todas", installmentsMin: "1", installmentsMax: "1", feePercent: "", firstSettlementDays: "30" };

const CardFeeRulesDialog = ({ open, onClose }) => {
  const isMobile = useIsMobile();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try { const d = await listCardFeeRules(); setRules(d.rules || []); }
    catch (e) { setRules([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { fetchRules(); setForm(blank); } }, [open, fetchRules]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const brandLabel = (b) => (CARD_BRANDS.find((x) => x.value === b) || {}).label || b;

  const handleSave = async () => {
    const fee = Number(String(form.feePercent).replace(",", "."));
    if (!(fee >= 0 && fee <= 100)) { toast.error("A taxa deve estar entre 0 e 100."); return; }
    setSaving(true);
    try {
      await upsertCardFeeRule({
        id: form.id || undefined, kind: form.kind, brand: form.brand,
        installmentsMin: Number(form.installmentsMin) || 1, installmentsMax: Number(form.installmentsMax) || 1,
        feePercent: fee, firstSettlementDays: Number(form.firstSettlementDays) || 0
      });
      toast.success("Regra salva.");
      setForm(blank); fetchRules();
    } catch (err) { toast.error(financeErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await deleteCardFeeRule(id); fetchRules(); }
    catch (err) { toast.error(financeErrorMessage(err)); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Regras de taxa do cartão</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Taxa (%) e prazo de recebimento (D+N) por bandeira, tipo e faixa de parcelas. "Todas as bandeiras" vale como padrão. Sem regra = taxa 0%.
        </Typography>
        <Box display="flex" style={{ gap: 8, flexWrap: "wrap", alignItems: "center", margin: "8px 0 16px" }}>
          <TextField select size="small" label="Tipo" value={form.kind} onChange={(e) => set("kind", e.target.value)} style={{ minWidth: 110 }}>
            <MenuItem value="debito">Débito</MenuItem>
            <MenuItem value="credito">Crédito</MenuItem>
          </TextField>
          <TextField select size="small" label="Bandeira" value={form.brand} onChange={(e) => set("brand", e.target.value)} style={{ minWidth: 150 }}>
            {CARD_BRANDS.map((b) => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
          </TextField>
          <TextField size="small" type="number" label="Parc. de" value={form.installmentsMin} onChange={(e) => set("installmentsMin", e.target.value)} style={{ width: 80 }} inputProps={{ min: 1 }} />
          <TextField size="small" type="number" label="até" value={form.installmentsMax} onChange={(e) => set("installmentsMax", e.target.value)} style={{ width: 70 }} inputProps={{ min: 1 }} />
          <TextField size="small" type="number" label="Taxa %" value={form.feePercent} onChange={(e) => set("feePercent", e.target.value)} style={{ width: 90 }} inputProps={{ step: "0.01", min: 0, max: 100 }} />
          <TextField size="small" type="number" label="D+N" value={form.firstSettlementDays} onChange={(e) => set("firstSettlementDays", e.target.value)} style={{ width: 80 }} inputProps={{ min: 0 }} />
          <Button variant="contained" color="primary" size="small" onClick={handleSave} disabled={saving}>
            {form.id ? "Salvar" : "Adicionar"}
          </Button>
          {form.id && <Button size="small" onClick={() => setForm(blank)}>Novo</Button>}
        </Box>
        {loading ? <Box display="flex" justifyContent="center" p={2}><CircularProgress /></Box>
          : rules.length === 0 ? <Typography variant="body2" color="textSecondary">Nenhuma regra cadastrada.</Typography>
          : (
            <div style={{ overflowX: "auto", width: "100%" }}>
            <Table size="small" style={{ minWidth: 640 }}>
              <TableHead><TableRow>
                <TableCell>Tipo</TableCell><TableCell>Bandeira</TableCell><TableCell align="center">Parcelas</TableCell>
                <TableCell align="right">Taxa</TableCell><TableCell align="center">D+N</TableCell><TableCell align="center" style={{ width: 90 }}>Ações</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rules.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.kind === "credito" ? "Crédito" : "Débito"}</TableCell>
                    <TableCell>{brandLabel(r.brand)}</TableCell>
                    <TableCell align="center">{r.installmentsMin}{r.installmentsMax !== r.installmentsMin ? `–${r.installmentsMax}` : ""}x</TableCell>
                    <TableCell align="right">{Number(r.feePercent)}%</TableCell>
                    <TableCell align="center">D+{r.firstSettlementDays}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => setForm({ id: r.id, kind: r.kind, brand: r.brand, installmentsMin: String(r.installmentsMin), installmentsMax: String(r.installmentsMax), feePercent: String(r.feePercent), firstSettlementDays: String(r.firstSettlementDays) })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
  );
};

export default CardFeeRulesDialog;
